import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users,
  ClipboardCheck,
  TrendingUp,
  StickyNote,
  Plane,
  Calendar,
  PartyPopper,
  Zap,
  Pin,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../api/api';
import { notesApi } from '../../../pages/Notes/notesApi';
import WeatherWidgetImpl from './WeatherWidget';
import WidgetHeader from './WidgetHeader';

import { toast } from "../../../utils/toast";
/* Helpers ------------------------------------------------------------- */

const authHeaderFromStorage = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fmtTime = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
};

/** Time-of-day greeting + matching emoji (recomputed as the clock ticks). */
const greetingFor = (date) => {
  const h = date.getHours();
  if (h < 12) return { text: 'Good Morning', emoji: '☀️' };
  if (h < 17) return { text: 'Good Afternoon', emoji: '🌤️' };
  return { text: 'Good Evening', emoji: '🌙' };
};

/** Prefer the stored first name; fall back to the first word of any name. */
const firstNameOf = (userData, fallback) => {
  const f = (userData?.first_name || '').trim();
  if (f) return f;
  const full = (fallback || '').trim();
  return full ? full.split(/\s+/)[0] : 'there';
};

/* Weather ------------------------------------------------------------- */

export const WeatherWidget = WeatherWidgetImpl;

/* Total Employees ----------------------------------------------------- */

export const TotalEmployeesWidget = () => {
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/team`,
          authHeaderFromStorage()
        );
        const count = Array.isArray(res.data)
          ? res.data.length
          : Array.isArray(res.data?.data)
            ? res.data.data.length
            : 0;
        if (!cancelled) setTotal(count);
      } catch (err) {
        console.error('Failed to fetch employees', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emp-compact">
      <span className="widget-head-iconpill emp-icon" aria-hidden="true">
        <Users size={16} />
      </span>
      <span className="emp-count">{loading ? '…' : total}</span>
      <div className="emp-meta">
        <span className="emp-title">Total Employees</span>
        <span className="emp-sub">Active workforce</span>
      </div>
    </div>
  );
};

/* Attendance Summary — live from /api/attendance/company --------------- */

export const AttendanceSummaryWidget = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/attendance/company`,
          authHeaderFromStorage()
        );
        if (!cancelled) setSummary(res.data?.summary || null);
      } catch (err) {
        console.error('[attendance-summary] fetch failed', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const total = summary?.total || 0;
  const present = (summary?.onTime || 0) + (summary?.earlyBird || 0);
  const absent = summary?.notMarked || 0;
  const late = summary?.late || 0;
  const onLeave = summary?.onLeave || 0;

  const pct = (n) => total > 0 ? Math.round((n / total) * 100) : 0;

  // Even 2×2 grid so every category — and its label — stays readable.
  const tile = (label, val, accent, bg) => ({ label, val, accent, bg, pct: pct(val) });
  const tiles = [
    tile('Present', present, '#1e9d77', '#f0fcf3'),
    tile('Late', late, '#e05d2a', '#fdf6eb'),
    tile('On Leave', onLeave, '#595ee9', '#f8f4fe'),
    tile('Absent', absent, '#d94b3f', '#fcf1f0'),
  ];

  const Tile = ({ label, val, accent, bg, pct: p }) => (
    <div className="att-tile" style={{ '--att-bg': bg, '--att-accent': accent }}>
      <span className="att-tile-label">{label}</span>
      <div className="att-tile-foot">
        <div className="att-tile-numrow">
          <span className="att-tile-num">{val}</span>
          <span className="att-tile-pct">({p}%)</span>
        </div>
        <div className="att-tile-bar">
          <div className="att-tile-bar-fill" style={{ width: `${p}%` }} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <WidgetHeader icon={ClipboardCheck} title="Today's Attendance Summary" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : (
        <div className="att-bento">
          {tiles.map((t) => <Tile key={t.label} {...t} />)}
        </div>
      )}
    </>
  );
};

/* Performance — live from /api/attendance/analytics ------------------- */

export const PerformanceWidget = () => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/attendance/analytics`,
          authHeaderFromStorage()
        );
        if (!cancelled) setKpis(res.data?.kpis || null);
      } catch (err) {
        console.error('[performance-widget] fetch failed', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const attRate = parseFloat(kpis?.attendanceRate || 0);
  const lateRate = parseFloat(kpis?.latePercentage || 0);
  const onTimeRate = Math.max(0, attRate - lateRate);

  const bars = [
    { label: 'Attendance Rate', pct: attRate, cls: 'att-range', color: '#22c55e' },
    { label: 'On-Time Rate', pct: onTimeRate, cls: 'ontim-range', color: '#6366f1' },
    { label: 'Late Rate', pct: lateRate, cls: 'late-range', color: '#f59e0b' },
  ];

  return (
    <>
      <WidgetHeader icon={TrendingUp} title="Performance" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : (
        <>
          {kpis?.earlyBirds > 0 && (
            <div style={{ fontSize: 12, marginBottom: 8, opacity: 0.7 }}>
              🌅 {kpis.earlyBirds} early bird{kpis.earlyBirds > 1 ? 's' : ''} today
            </div>
          )}
          {bars.map(({ label, pct, cls, color }) => (
            <div key={cls} className={`item ${cls}`} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <label style={{ fontWeight: 600 }}>{label}</label>
                <span style={{ fontWeight: 700, color }}>{pct.toFixed(1)}%</span>
              </div>
              <div className="range-bar" style={{ height: 7, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }}>
                <div className="bar" style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }}></div>
              </div>
            </div>
          ))}
          {kpis?.onLeaveToday > 0 && (
            <div style={{ fontSize: 12, marginTop: 6, opacity: 0.65 }}>
              ✈️ {kpis.onLeaveToday} on leave today
            </div>
          )}
        </>
      )}
    </>
  );
};

/* Leave balance — remaining days by type for the current user ---------- */
export const LeaveBalanceWidget = () => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const user = JSON.parse(localStorage.getItem('userdata') || '{}');
      if (!user.id) { setLoading(false); return; }
      try {
        const res = await axios.get(
          `${API_URL}/api/leaves/balance/${user.id}`,
          authHeaderFromStorage()
        );
        if (!cancelled) setBalances(res.data?.balances || []);
      } catch (err) {
        console.error('[leave-balance] fetch failed', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <WidgetHeader icon={Plane} title="My Leave Balance" />
      {loading ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>Loading…</p>
      ) : balances.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: '8px 0' }}>No leave types configured.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          {balances.map((b) => {
            const max = Number(b.max_days_per_year) || 0;
            const used = Number(b.used_days) || 0;
            const remaining = b.remaining_days != null ? Number(b.remaining_days) : max - used;
            const pct = max > 0 ? Math.max(0, Math.min(100, Math.round((used / max) * 100))) : 0;
            return (
              <div key={b.leave_type_id} onClick={() => navigate('/leaves')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, marginBottom: 4 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 600 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: b.color || '#94a3b8', display: 'inline-block' }} />
                    {b.name}
                    {b.is_paid === 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: 'rgba(234,179,8,0.14)', borderRadius: 6, padding: '1px 6px' }}>UNPAID</span>
                    )}
                  </span>
                  <span style={{ fontWeight: 700 }}>{remaining}<span style={{ opacity: 0.5, fontWeight: 500 }}> / {max} left</span></span>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }}>
                  <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: b.color || '#94a3b8', transition: 'width 0.5s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/* Star of the Month --------------------------------------------------- */
// Implementation lives in StarOfMonthWidget.js (themed card + flip + confetti).
export { StarOfTheMonthWidget } from './StarOfMonthWidget';

// Build initials for an avatar fallback from a full name (used by the
// Employees-on-Leave widget below).
const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');

/* Date / Time + Mark In/Out ------------------------------------------ */

/** Circular worked-hours ring. `frac` 0–1; rotated so it fills from the top. */
const WorkRing = ({ frac, variant }) => {
  const R = 19;
  const C = 2 * Math.PI * R;
  return (
    <svg className={`att-ring att-ring--${variant}`} viewBox="0 0 44 44" aria-hidden="true">
      <circle className="att-ring-track" cx="22" cy="22" r={R} />
      <circle
        className="att-ring-fill"
        cx="22"
        cy="22"
        r={R}
        style={{ strokeDasharray: C, strokeDashoffset: C * (1 - Math.max(0, Math.min(1, frac))) }}
      />
    </svg>
  );
};

export const DateTimeAttendanceWidget = () => {
  const userData = JSON.parse(localStorage.getItem('userdata') || '{}');
  const username = localStorage.getItem('username') || 'Admin';
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const userId = userData.id;

  const apiBase = `${API_URL}/api/attendance`;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayRec, setTodayRec] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(
          `${apiBase}/today/${userId}`,
          authHeaderFromStorage()
        );
        if (!cancelled && res.data) {
          // Normalize datetime strings from server ("YYYY-MM-DD HH:MM:SS") to
          // ISO format so new Date() parses them as local time, not UTC.
          const d = res.data;
          if (d.mark_in) d.mark_in = String(d.mark_in).replace(' ', 'T');
          if (d.mark_out) d.mark_out = String(d.mark_out).replace(' ', 'T');
          setTodayRec(d);
        } else if (!cancelled) {
          setTodayRec(null);
        }
      } catch (err) {
        console.error('Failed to fetch today attendance', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const attendanceStatus = !todayRec
    ? 'not-marked'
    : todayRec.status === 'in-progress'
      ? 'in-progress'
      : 'present';

  const firstName = firstNameOf(userData, username);
  const greeting = greetingFor(currentTime);

  // Clock parts — split so seconds + AM/PM can be styled down independently.
  const h24 = currentTime.getHours();
  const clock = {
    hm: `${h24 % 12 || 12}:${String(currentTime.getMinutes()).padStart(2, '0')}`,
    ss: String(currentTime.getSeconds()).padStart(2, '0'),
    ampm: h24 < 12 ? 'AM' : 'PM',
  };

  // Worked-hours ring — elapsed since mark-in (live while in-progress, frozen
  // at mark-out once present) as a fraction of a standard 8-hour workday.
  const markInDate = todayRec?.mark_in ? new Date(todayRec.mark_in) : null;
  const markOutDate = todayRec?.mark_out ? new Date(todayRec.mark_out) : null;
  const hasIn = markInDate && !isNaN(markInDate.getTime());
  const endRef =
    attendanceStatus === 'present' && markOutDate && !isNaN(markOutDate.getTime())
      ? markOutDate
      : currentTime;
  // Subtract break time, same as AttendanceCard. This matters now that marking
  // in again after a mark-out reopens the day and books the gap as a break —
  // without it the ring would count the hours spent away.
  const breakMs = Number(todayRec?.break_seconds || 0) * 1000;
  const workedMs = hasIn ? Math.max(0, endRef - markInDate - breakMs) : 0;
  const workedLabel = `${Math.floor(workedMs / 3600000)}h ${String(
    Math.floor((workedMs % 3600000) / 60000)
  ).padStart(2, '0')}m`;
  const workedFrac = Math.min(workedMs / (8 * 3600000), 1);

  const handleMarkIn = async () => {
    setLoadingAttendance(true);
    try {
      const res = await axios.post(
        `${apiBase}/mark-in`,
        { user_id: userId },
        authHeaderFromStorage()
      );
      // Use the server-returned record so the displayed time matches storage.
      // The server stores mark_in as a local datetime string (e.g. "2026-05-14 09:15:00").
      // Convert space→T so Date() parses it as local time, not UTC.
      const rec = res.data || {};
      const markInIso = rec.mark_in
        ? String(rec.mark_in).replace(' ', 'T')
        : new Date().toISOString();
      setTodayRec({ ...rec, status: 'in-progress', mark_in: markInIso });
    } catch (err) {
      console.error(err);
      // Fallback: re-fetch to get the real state.
      try {
        const r2 = await axios.get(`${apiBase}/today/${userId}`, authHeaderFromStorage());
        if (r2.data) {
          const d = r2.data;
          if (d.mark_in) d.mark_in = String(d.mark_in).replace(' ', 'T');
          if (d.mark_out) d.mark_out = String(d.mark_out).replace(' ', 'T');
          setTodayRec(d);
        }
      } catch { }
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleMarkOut = async () => {
    setLoadingAttendance(true);
    try {
      const res = await axios.post(
        `${apiBase}/mark-out`,
        { user_id: userId },
        authHeaderFromStorage()
      );
      const rec = res.data || {};
      const markOutIso = rec.mark_out
        ? String(rec.mark_out).replace(' ', 'T')
        : new Date().toISOString();
      setTodayRec((prev) => ({ ...prev, ...rec, status: 'present', mark_out: markOutIso }));
    } catch (err) {
      console.error(err);
      try {
        const r2 = await axios.get(`${apiBase}/today/${userId}`, authHeaderFromStorage());
        if (r2.data) {
          const d = r2.data;
          if (d.mark_in) d.mark_in = String(d.mark_in).replace(' ', 'T');
          if (d.mark_out) d.mark_out = String(d.mark_out).replace(' ', 'T');
          setTodayRec(d);
        }
      } catch { }
    } finally {
      setLoadingAttendance(false);
    }
  };

  return (
    <>
      <div className="date-time">
        <div className="greet-wrap">
          {greeting.text}, <span className="greet-name">{firstName}</span>!{' '}
          <span className="greet-emoji" aria-hidden="true">
            {greeting.emoji}
          </span>
        </div>
        <div className="itme-wrap">
          <span className="t-hm">{clock.hm}</span>
          <span className="t-sec">:{clock.ss}</span>
          <span className="t-ampm">{clock.ampm}</span>
        </div>
        <div className="date-wrap">
          {currentTime.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>
      <div className="att-mark">
        {attendanceStatus === 'not-marked' && (
          <>
            <button onClick={handleMarkIn} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking In...' : 'Mark In'}
            </button>
            <div className="att-pill att-pill--idle">
              <span className="att-pill-dot" />
              Not marked yet
            </div>
          </>
        )}
        {attendanceStatus === 'in-progress' && (
          <>
            <button onClick={handleMarkOut} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking Out...' : 'Mark Out'}
            </button>
            <div className="att-progress">
              <WorkRing frac={workedFrac} variant="active" />
              <div className="att-progress-info">
                <span className="att-progress-time">{workedLabel}</span>
                <span className="att-progress-sub">
                  since {fmtTime(todayRec?.mark_in)}
                </span>
              </div>
            </div>
          </>
        )}
        {attendanceStatus === 'present' && (
          <>
            {/* Marking out doesn't close the day — someone who stepped out (or
                marked out by mistake) can mark in again. The server reopens
                today's row and books the gap as a break, so hours stay right. */}
            <button onClick={handleMarkIn} disabled={loadingAttendance}>
              {loadingAttendance ? 'Marking In...' : 'Mark In'}
            </button>
            <div className="att-progress">
              <WorkRing frac={workedFrac} variant="done" />
              <div className="att-progress-info">
                <span className="att-progress-time">{workedLabel}</span>
                <span className="att-progress-sub">
                  {fmtTime(todayRec?.mark_in)}–{fmtTime(todayRec?.mark_out)}
                </span>
              </div>
            </div>
          </>
        )}
        <button
          type="button"
          className="att-link"
          onClick={() => (window.location.href = `/${role}/attendance`)}
        >
          View full attendance
          <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
};

/* Member Actions (Request Leave / Request Shift) --------------------- */

const ShiftRequestModal = ({ show, onClose, token }) => {
  const baseURL = `${API_URL}/api`;
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const user = JSON.parse(localStorage.getItem('userdata') || '{}');

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`${baseURL}/settings/shifts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!cancelled) setShifts(res.data || []);
      } catch {
        if (!cancelled) setError('Failed to load shifts.');
      }
    };
    fetchShifts();
    return () => {
      cancelled = true;
    };
  }, [show, token, baseURL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!shiftId) return setError('Please select a shift.');
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseURL}/settings/shifts/shift-request`,
        { user_id: user?.id, shift_id: shiftId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      if (res.status === 200 || res.status === 201) {
        toast.success('Shift request submitted successfully!');
        setShiftId('');
        onClose();
      } else {
        setError('Failed to submit request.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Error submitting request.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <h4>Request Shift</h4>
        <form className="modal-body" onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-item">
            <label>Select Shift</label>
            <select
              value={shiftId}
              onChange={(e) => setShiftId(e.target.value)}
              required
            >
              <option value="">Select shift</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <footer className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="orng-fill" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

const LeaveRequestModal = ({ show, onClose, token }) => {
  const getMaxPastLeaveDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 15);
    return d.toISOString().split('T')[0];
  };
  const maxPastDate = getMaxPastLeaveDate();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const baseURL = `${API_URL}/api`;
  const emptyLeaveForm = {
    leave_type_id: '',
    duration_type: 'full_day',
    start_date: '',
    end_date: '',
    reason: '',
  };
  const [form, setForm] = useState(emptyLeaveForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const fetchLeaveTypes = async () => {
      try {
        const res = await axios.get(
          `${baseURL}/settings/calendar/leave-types`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );
        if (!cancelled) setLeaveTypes(res.data || []);
      } catch {
        if (!cancelled) setError('Failed to load leave types.');
      }
    };
    fetchLeaveTypes();
    return () => {
      cancelled = true;
    };
  }, [show, token, baseURL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (name === 'duration_type' && value !== 'full_day') {
        return { ...prev, duration_type: value, end_date: prev.start_date };
      }
      if (name === 'start_date' && prev.duration_type !== 'full_day') {
        return { ...prev, start_date: value, end_date: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      return setError('Please select leave type and dates.');
    }
    const user = JSON.parse(localStorage.getItem('userdata') || '{}');
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseURL}/leaves/request`,
        {
          user_id: user?.id,
          leave_type_id: form.leave_type_id,
          duration_type: form.duration_type,
          start_date: form.start_date,
          end_date: form.end_date,
          reason: form.reason,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      if (res.status === 201 || res.status === 200) {
        onClose();
        setForm(emptyLeaveForm);
      } else {
        setError('Failed to submit leave request.');
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || 'An error occurred while submitting.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <header className="modal-header"><h4>Request Leave</h4></header>
        <form className="modal-body" onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-item">
            <label>Leave Type</label>
            <select
              name="leave_type_id"
              value={form.leave_type_id}
              onChange={handleChange}
              required
            >
              <option value="">Select leave type</option>
              {leaveTypes.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-item">
            <label>Duration Type</label>
            <select
              name="duration_type"
              value={form.duration_type}
              onChange={handleChange}
              required
            >
              <option value="full_day">Full Day</option>
              <option value="half_day_first">Half Day – First Half</option>
              <option value="half_day_second">Half Day – Second Half</option>
            </select>
          </div>
          <div className="form-item">
            <label>Start Date</label>
            <input
              type="date"
              name="start_date"
              value={form.start_date}
              min={maxPastDate}
              onChange={handleChange}
              required
            />
          </div>
          {form.duration_type === 'full_day' && (
            <div className="form-item">
              <label>End Date</label>
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={handleChange}
                min={maxPastDate}
                required
              />
            </div>
          )}
          <div className="form-item">
            <label>Reason (optional)</label>
            <textarea
              name="reason"
              value={form.reason}
              onChange={handleChange}
              rows={3}
            />
          </div>
          <footer className="modal-footer">
            <button type="button" onClick={onClose} className="btn">
              Cancel
            </button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export const MemberActionsWidget = () => {
  const token = localStorage.getItem('token');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);

  return (
    <>
      <WidgetHeader icon={Zap} title="Quick Actions" />
      <button
        className="btn btn-primary"
        onClick={() => setShowLeaveModal(true)}
      >
        Request Leave
      </button>
      <LeaveRequestModal
        show={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        token={token}
      />
      <button onClick={() => setShowShiftModal(true)}>Request Shift</button>
      <ShiftRequestModal
        show={showShiftModal}
        onClose={() => setShowShiftModal(false)}
        token={token}
      />
    </>
  );
};

/* Personal Notes ------------------------------------------------------ */

// Dashboard preview of the user's notes: shows the pinned (or most-recent)
// note and links to the full Notes page. Full CRUD lives on /notes.
const notesBasePath = () => {
  const role = localStorage.getItem('role');
  if (role === 'Admin') return '/admin';
  if (role === 'HR') return '/hr';
  return '/member';
};
const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

// "10 Jun" — the note's automatic created-date tag (short form for stickies).
const noteDateTag = (ts) => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

// Extract checklist items from a note body (TipTap taskList HTML).
// Returns null when the note has no checklist so callers fall back to a snippet.
const parseTaskItems = (html) => {
  if (!html || !html.includes('data-type="taskList"')) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const items = Array.from(doc.querySelectorAll('ul[data-type="taskList"] > li'));
  if (!items.length) return null;
  return items.map((li) => ({
    checked: li.getAttribute('data-checked') === 'true',
    text: (li.textContent || '').replace(/\s+/g, ' ').trim(),
  }));
};

// Flip the nth checklist item in the note body and return the updated HTML.
const toggleTaskInHtml = (html, index) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const items = doc.querySelectorAll('ul[data-type="taskList"] > li');
  const li = items[index];
  if (!li) return html;
  const next = li.getAttribute('data-checked') !== 'true';
  li.setAttribute('data-checked', String(next));
  const input = li.querySelector('input[type="checkbox"]');
  if (input) {
    if (next) input.setAttribute('checked', 'checked');
    else input.removeAttribute('checked');
  }
  return doc.body.innerHTML;
};

// Paper tint for a sticky: the note's label color washed into warm paper.
const stickyPaper = (color) =>
  color
    ? `color-mix(in srgb, ${color} 14%, #fffdf2)`
    : '#fff9db';

// One sticky note on the wall. Checklist items are live — ticking saves.
const StickyNoteCard = ({ note, onOpen, onToggleTask }) => {
  const tasks = parseTaskItems(note.body);
  const shown = tasks ? tasks.slice(0, 3) : null;
  const doneCount = tasks ? tasks.filter((t) => t.checked).length : 0;
  const snippet = !tasks ? stripHtml(note.body) : '';

  return (
    <div
      role="button"
      tabIndex={0}
      className="pn-sticky"
      style={{ '--pn-paper': stickyPaper(note.color) }}
      onClick={() => onOpen(note.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(note.id); }}
    >
      {!!note.pinned && (
        <span className="pn-sticky-pin" title="Pinned">
          <Pin size={12} />
        </span>
      )}
      <div className="pn-sticky-title">{note.title || 'Untitled note'}</div>

      {shown ? (
        <div className="pn-sticky-tasks">
          {shown.map((t, i) => (
            <label
              key={i}
              className={`pn-task${t.checked ? ' done' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={t.checked}
                onChange={() => onToggleTask(note, i)}
              />
              <span>{t.text || 'To-do'}</span>
            </label>
          ))}
          {tasks.length > 3 && (
            <span className="pn-task-more">+{tasks.length - 3} more</span>
          )}
        </div>
      ) : (
        <div className="pn-sticky-snippet">{snippet || 'No content yet'}</div>
      )}

      <div className="pn-sticky-foot">
        <span className="pn-sticky-date">{noteDateTag(note.created_at)}</span>
        {tasks && tasks.length > 0 && (
          <span className={`pn-sticky-count${doneCount === tasks.length ? ' all-done' : ''}`}>
            {doneCount}/{tasks.length}
          </span>
        )}
      </div>
    </div>
  );
};

export const PersonalNotesWidget = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const list = await notesApi.list();
      setNotes(Array.isArray(list) ? list : []);
    } catch (err) {
      console.warn('[personal-notes] fetch failed', err?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    notesApi
      .list()
      .then((list) => !cancelled && setNotes(Array.isArray(list) ? list : []))
      .catch((err) => console.warn('[personal-notes] fetch failed', err?.message))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Backend returns pinned-first, then most-recent. Wall shows the first 4.
  const wall = notes.slice(0, 4);
  const goNotes = () => navigate(`${notesBasePath()}/notes`);
  const openNote = (id) => navigate(`${notesBasePath()}/notes?note=${id}`);

  // Tick a checklist item straight from the wall — optimistic, then save.
  const handleToggleTask = async (note, taskIndex) => {
    const nextHtml = toggleTaskInHtml(note.body, taskIndex);
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, body: nextHtml } : n)));
    try {
      await notesApi.update(note.id, { body: nextHtml });
    } catch (err) {
      console.error('[personal-notes] task toggle failed', err);
      refresh(); // roll back to server truth
    }
  };

  // Inline quick-add: create from the title field and stay on the dashboard.
  const handleQuickAdd = async (e) => {
    e.preventDefault();
    const title = draft.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      await notesApi.create({ title, body: '' });
      setDraft('');
      await refresh();
    } catch (err) {
      console.error('[personal-notes] quick-add failed', err);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <WidgetHeader
        icon={StickyNote}
        title="Personal Notes"
        action={
          <span className="pn-head-actions">
            <button type="button" className="pn-open" title="Open Notes" onClick={goNotes}>
              Open <ChevronRight size={13} />
            </button>
          </span>
        }
      />
      <div className="content personal-notes-content">
        {/* Inline quick-add */}
        <form className="pn-quickadd" onSubmit={handleQuickAdd}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Quick note…"
            aria-label="Quick note title"
          />
          <button type="submit" className="pn-quickadd-btn" disabled={!draft.trim() || adding} title="Add note">
            <Plus size={16} />
          </button>
        </form>

        {!loaded ? (
          <div className="pn-empty">Loading your notes…</div>
        ) : wall.length === 0 ? (
          <div className="pn-empty">
            <StickyNote size={20} color="#f54b01" />
            No notes yet — jot one above.
          </div>
        ) : (
          <>
            <div className="pn-wall">
              {wall.map((n) => (
                <StickyNoteCard
                  key={n.id}
                  note={n}
                  onOpen={openNote}
                  onToggleTask={handleToggleTask}
                />
              ))}
            </div>
            {notes.length > wall.length && (
              <button type="button" className="pn-more" onClick={goNotes}>
                +{notes.length - wall.length} more note{notes.length - wall.length === 1 ? '' : 's'}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};


/* Employees on Leave Today ------------------------------------------- */

export const EmployeesOnLeaveWidget = () => {
  const [todayLeaves, setTodayLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/api/leaves/all`,
          authHeaderFromStorage()
        );
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const filtered = (res.data || []).filter((leave) => {
          if (leave.status !== 'Approved') return false;
          const start = new Date(leave.start_date);
          const end = new Date(leave.end_date);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          return today >= start && today <= end;
        });
        if (!cancelled) setTodayLeaves(filtered);
      } catch (err) {
        console.error("Failed to fetch today's leaves", err);
        if (!cancelled) setTodayLeaves([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const count = todayLeaves.length;

  // Tint leave rows by leave-type keyword.
  const leaveTypeStyle = (type = '') => {
    const t = type.toLowerCase();
    if (/sick|medical/.test(t)) return { accent: '#ef4444', emoji: '🤒' };
    if (/casual/.test(t)) return { accent: '#0ea5e9', emoji: '🏖️' };
    if (/vacation|annual|earned/.test(t)) return { accent: '#14b8a6', emoji: '🏝️' };
    if (/maternity|paternity|parental/.test(t)) return { accent: '#ec4899', emoji: '👶' };
    if (/bereavement/.test(t)) return { accent: '#64748b', emoji: '🕊️' };
    if (/wfh|remote|work[- ]from/.test(t)) return { accent: '#8b5cf6', emoji: '💻' };
    return { accent: '#f59e0b', emoji: '✈️' };
  };

  const handleSeeAll = () => {
    window.location.href = `/${role}/leaves`;
  };

  const [hero, ...rest] = todayLeaves;

  return (
    <div className="event-panel">
      <SectionHeader
        icon={Plane}
        title="Employees on Leave Today"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading…</p>
      ) : count > 0 ? (
        <div className="event-list">
          <div className="leave-summary">
            <div className="leave-summary-stack" aria-hidden="true">
              {todayLeaves.slice(0, 4).map((l, i) => (
                <span
                  key={l.id || i}
                  className="leave-summary-avatar"
                  style={{ zIndex: 4 - i }}
                >
                  {initialsOf(l.employee_name || '')}
                </span>
              ))}
              {count > 4 && (
                <span className="leave-summary-more">+{count - 4}</span>
              )}
            </div>
            <div className="leave-summary-text">
              <b>{count}</b> {count === 1 ? 'person' : 'people'} out today
              <p>{hero?.employee_name} {rest.length ? `and ${rest.length} more` : ''}</p>
            </div>
          </div>
          {todayLeaves.map((leave) => {
            const { emoji } = leaveTypeStyle(leave.leave_type);
            return (
              <div className="event-row" key={leave.id}>
                <span className="event-row-emoji" aria-hidden="true">{emoji}</span>
                <div className="event-row-body">
                  <h5 className="event-row-title">{leave.employee_name}</h5>
                  <p className="event-row-sub">{leave.leave_type}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="leave-empty">
          <div className="leave-empty-icon" aria-hidden="true">✅</div>
          <b>All hands on deck</b>
          <p>No employees on leave today</p>
        </div>
      )}
    </div>
  );
};

/* =====================================================================
   Upcoming Holidays & Celebrations — shared helpers
   ===================================================================== */

const MONTH_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Days between two dates (ignoring time-of-day).
const daysUntil = (isoDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const countdownLabel = (isoDate) => {
  const d = daysUntil(isoDate);
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  if (d < 7) return `in ${d} days`;
  if (d < 14) return 'Next week';
  if (d < 31) return `in ${Math.round(d / 7)} wks`;
  return `in ${Math.round(d / 30)} mo`;
};

const dateChip = (isoDate) => {
  const d = new Date(isoDate);
  return { day: d.getDate(), mon: MONTH_SHORT[d.getMonth()] };
};

// Emoji matcher — falls back to category, then default.
const HOLIDAY_EMOJI_MAP = [
  [/christmas|xmas/i, '🎄', '#10b981'],
  [/new\s*year/i, '🎆', '#6366f1'],
  [/diwali|deepavali/i, '🪔', '#f59e0b'],
  [/holi/i, '🎨', '#ec4899'],
  [/eid/i, '🌙', '#14b8a6'],
  [/easter/i, '🐣', '#f472b6'],
  [/thanksgiving/i, '🦃', '#d97706'],
  [/halloween/i, '🎃', '#f97316'],
  [/valentine/i, '💝', '#ef4444'],
  [/independence|republic|national/i, '🎌', '#0ea5e9'],
  [/labor|labour/i, '🛠️', '#64748b'],
];

const pickHolidayStyle = (name) => {
  for (const [rx, emoji, color] of HOLIDAY_EMOJI_MAP) {
    if (rx.test(name)) return { emoji, color };
  }
  return { emoji: '📅', color: '#0ea5e9' };
};

const CELEBR_EMOJI_MAP = [
  [/birthday|b-?day/i, '🎂', '#ec4899'],
  [/anniversary/i, '🏆', '#f59e0b'],
  [/wedding|marriage/i, '💒', '#f472b6'],
  [/milestone|goal/i, '🎯', '#8b5cf6'],
  [/promotion|promoted/i, '⭐', '#0ea5e9'],
  [/achievement|award/i, '🏅', '#eab308'],
  [/baby|newborn/i, '👶', '#22d3ee'],
  [/retire/i, '🎖️', '#64748b'],
];

const pickCelebrationStyle = (name) => {
  for (const [rx, emoji, color] of CELEBR_EMOJI_MAP) {
    if (rx.test(name)) return { emoji, color };
  }
  return { emoji: '🎉', color: '#f54900' };
};

/** A glass hero card — used for the nearest upcoming event. */
const HeroEventCard = ({ emoji, accent, title, subtitle, isoDate }) => {
  const { day, mon } = dateChip(isoDate);
  return (
    <div className="event-hero" style={{ '--accent': accent }}>
      <span className="event-hero-emoji" aria-hidden="true">{emoji}</span>
      <div className="event-hero-mid">
        <div className="event-hero-countdown">{countdownLabel(isoDate)}</div>
        <h4 className="event-hero-title">{title}</h4>
        {subtitle && <p className="event-hero-sub">{subtitle}</p>}
      </div>
      <div className="event-hero-chip" aria-hidden="true">
        <b>{day}</b>
        <span>{mon}</span>
      </div>
    </div>
  );
};

/** A compact row — neutral tint with a 4px accent stripe on the left. */
const EventRow = ({ emoji, accent, title, subtitle, isoDate }) => {
  const { day, mon } = dateChip(isoDate);
  return (
    <div className="event-row" style={{ '--accent': accent }}>
      <span className="event-row-emoji" aria-hidden="true">{emoji}</span>
      <div className="event-row-body">
        <h5 className="event-row-title">{title}</h5>
        {subtitle && <p className="event-row-sub">{subtitle}</p>}
      </div>
      <div className="event-row-meta">
        <span className="event-row-chip">{day} {mon}</span>
        <span className="event-row-countdown">{countdownLabel(isoDate)}</span>
      </div>
    </div>
  );
};

/** Shared header with built-in "See all →" button. */
const SectionHeader = ({ icon, title, onSeeAll }) => (
  <div className="widget-head-row">
    <WidgetHeader icon={icon} title={title} />
    <button
      type="button"
      className="widget-head-seeall"
      onClick={onSeeAll}
    >
      See all →
    </button>
  </div>
);

/* Upcoming Holidays --------------------------------------------------- */

/**
 * Shift a recurring holiday forward by whole years until its date is on/after
 * today. Non-recurring holidays pass through unchanged. Returns ISO YYYY-MM-DD.
 */
const effectiveHolidayDate = (isoDate, repeatAnnually) => {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;

  if (repeatAnnually) {
    while (d < today) d.setFullYear(d.getFullYear() + 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const UpcomingHolidaysWidget = () => {
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_URL}/api/settings/calendar/holidays`,
          authHeaderFromStorage()
        );
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const normalised = list
          .map((h) => {
            const date = effectiveHolidayDate(h.date, !!h.repeat_annually);
            return date
              ? {
                id: h.id,
                name: h.name,
                type: h.type,
                date,
              }
              : null;
          })
          .filter(Boolean)
          .filter((h) => daysUntil(h.date) >= 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setHolidays(normalised);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const [hero, ...rest] = holidays;
  const handleSeeAll = () => {
    window.location.href = `/${role}/settings/calendar`;
  };

  return (
    <div className="event-panel">
      <SectionHeader
        icon={Calendar}
        title="Upcoming Holidays"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading holidays…</p>
      ) : error ? (
        <p className="event-empty">Couldn't load holidays.</p>
      ) : hero ? (
        <div className="event-list">
          <HeroEventCard
            {...pickHolidayStyle(hero.name)}
            title={hero.name}
            subtitle={hero.type}
            isoDate={hero.date}
          />
          {rest.map((h) => (
            <EventRow
              key={h.id}
              {...pickHolidayStyle(h.name)}
              title={h.name}
              subtitle={h.type}
              isoDate={h.date}
            />
          ))}
        </div>
      ) : (
        <p className="event-empty">No upcoming holidays.</p>
      )}
    </div>
  );
};

/* Upcoming Celebrations ---------------------------------------------- */

/**
 * Build the next occurrence of an anniversary-style date (dob / join_date).
 * Rolls the month+day forward into current or next year so it's always
 * on/after today. Returns { isoDate, years } where `years` is completed
 * years since the source date at the next occurrence (0 for birthdays
 * where we don't show the count).
 */
const nextAnniversary = (isoDate) => {
  if (!isoDate) return null;
  const src = new Date(isoDate);
  if (Number.isNaN(src.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const candidate = new Date(today.getFullYear(), src.getMonth(), src.getDate());
  if (candidate < today) candidate.setFullYear(candidate.getFullYear() + 1);

  const y = candidate.getFullYear();
  const m = String(candidate.getMonth() + 1).padStart(2, '0');
  const d = String(candidate.getDate()).padStart(2, '0');
  const years = y - src.getFullYear();
  return { isoDate: `${y}-${m}-${d}`, years };
};

const fullNameOf = (m) => {
  const f = (m.first_name || '').trim();
  const l = (m.last_name || '').trim();
  const joined = `${f} ${l}`.trim();
  return joined || m.name || m.email || 'Team member';
};

export const UpcomingCelebrationsWidget = () => {
  const role = (localStorage.getItem('role') || 'member').toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_URL}/api/team`,
          authHeaderFromStorage()
        );
        if (cancelled) return;
        const members = Array.isArray(res.data) ? res.data : [];
        const events = [];
        members.forEach((m) => {
          const name = fullNameOf(m);

          // Birthday — from dob
          const dob = m.dob || m.date_of_birth || null;
          const bday = nextAnniversary(dob);
          if (bday) {
            events.push({
              id: `bday-${m.id || name}`,
              name: `${name}'s Birthday`,
              type: 'Birthday',
              date: bday.isoDate,
            });
          }

          // Work Anniversary — from join_date (skip year 0)
          const doj = m.join_date || m.date_of_joining || m.doj || null;
          const anniv = nextAnniversary(doj);
          if (anniv && anniv.years > 0) {
            events.push({
              id: `anniv-${m.id || name}`,
              name: `${name} · ${anniv.years} Year${anniv.years > 1 ? 's' : ''} Anniversary`,
              type: 'Work Anniversary',
              date: anniv.isoDate,
            });
          }
        });

        const normalised = events
          .filter((c) => daysUntil(c.date) >= 0)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 3);
        setItems(normalised);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const [hero, ...rest] = items;
  const handleSeeAll = () => {
    window.location.href = `/${role}/teams`;
  };

  return (
    <div className="event-panel">
      <SectionHeader
        icon={PartyPopper}
        title="Upcoming Celebrations"
        onSeeAll={handleSeeAll}
      />
      {loading ? (
        <p className="event-empty">Loading celebrations…</p>
      ) : error ? (
        <p className="event-empty">Couldn't load celebrations.</p>
      ) : hero ? (
        <div className="event-list">
          <HeroEventCard
            {...pickCelebrationStyle(hero.name + ' ' + hero.type)}
            title={hero.name}
            subtitle={hero.type}
            isoDate={hero.date}
          />
          {rest.map((c) => (
            <EventRow
              key={c.id}
              {...pickCelebrationStyle(c.name + ' ' + c.type)}
              title={c.name}
              subtitle={c.type}
              isoDate={c.date}
            />
          ))}
        </div>
      ) : (
        <p className="event-empty">No upcoming celebrations.</p>
      )}
    </div>
  );
};
